import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Archive as ArchiveIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';

/**
 * ImportView provides telemetry archive import functionality
 * Allows users to upload archive files and import telemetry data
 */
function ImportView() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [importMode, setImportMode] = useState('append');
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [archiveManifest, setArchiveManifest] = useState(null);
  const [showManifestDialog, setShowManifestDialog] = useState(false);
  
  const fileInputRef = useRef();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (file.type === 'application/zip' || file.name.endsWith('.zip')) {
        setSelectedFile(file);
        setMessage('');
        // Parse manifest if possible
        parseArchiveManifest(file);
      } else {
        setMessage('Please select a valid ZIP archive file');
        setMessageType('error');
        setSelectedFile(null);
      }
    }
  };

  const parseArchiveManifest = async (file) => {
    try {
      // This would require File System Access API or similar
      // For now, we'll show a placeholder manifest
      let manifest = {
        archiveId: 'sample-archive-id',
        exportDate: new Date().toISOString(),
        fleetInfo: {
          fleetId: 'sample-fleet',
          fleetName: 'Sample Fleet',
          customerId: 'sample-customer',
          customerName: 'Sample Customer'
        },
        dateRange: {
          start: '2024-06-01T00:00:00Z',
          end: '2024-06-15T23:59:59Z'
        },
        summary: {
          totalCanopies: 3,
          totalRecords: 6480,
          sizeBytes: 2547892
        },
        canopies: [
          {
            canopyId: 'canopy-001',
            filename: 'canopies/canopy-001.json',
            recordCount: 2160,
            dateRange: {
              start: '2024-06-01T00:15:00Z',
              end: '2024-06-15T23:45:00Z'
            }
          }
        ]
      };

      // Compatibility shim for old manifests
      if (file) {
        // Read the file as text
        const text = await file.text();
        let rawManifest = {};
        try {
          rawManifest = JSON.parse(text);
        } catch (e) {
          throw new Error('Invalid manifest JSON');
        }

        // Detect old manifest by presence of export_id or fleet_id at top level
        if (rawManifest.export_id || rawManifest.fleet_id || rawManifest.canopy_metadata) {
          // Convert old manifest to new format
          manifest = {
            archiveId: rawManifest.export_id || '',
            exportDate: rawManifest.export_date || '',
            fleetInfo: {
              fleetId: rawManifest.fleet_id || '',
              fleetName: (rawManifest.fleet_data && (rawManifest.fleet_data.fleet_name || rawManifest.fleet_data.fleet_description)) || '',
              customerId: (rawManifest.fleet_data && rawManifest.fleet_data.customer_id) || '',
              customerName: (rawManifest.fleet_data && rawManifest.fleet_data.customer_name) || ''
            },
            dateRange: rawManifest.date_range || { start: '', end: '' },
            summary: {
              totalCanopies: (rawManifest.export_summary && rawManifest.export_summary.canopies_exported) || 0,
              totalRecords: (rawManifest.export_summary && rawManifest.export_summary.total_records) || 0,
              sizeBytes: 0 // Not available in old manifest
            },
            canopies: (rawManifest.canopy_metadata || []).map((c) => ({
              canopyId: c.canopy_id,
              canopyName: c.canopy_name || '',
              filename: `canopies/${c.canopy_id}.json`,
              recordCount: c.record_count || 0,
              dateRange: {
                start: c.earliest_record || '',
                end: c.latest_record || ''
              },
              sampleData: c.sampleData || {}
            }))
          };
        } else {
          // Assume new manifest is already in correct format
          manifest = rawManifest;
        }
      }
      setArchiveManifest(manifest);
    } catch (error) {
      console.log('Could not parse manifest:', error);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setMessage('Please select an archive file to import');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('archive', selectedFile);
      formData.append('importMode', importMode);
      formData.append('confirmOverwrite', confirmOverwrite);

      const response = await fetch(
        'https://sabalsql-dbam-ji2csljmda-uk.a.run.app/api/archive/import',
        {
          method: 'POST',
          body: formData
        }
      );

      const result = await response.json();

      if (result.status === 'success') {
        setMessage('Archive imported successfully!');
        setMessageType('success');
        setSelectedFile(null);
        setArchiveManifest(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setMessage(result.message || 'Import failed');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Import failed: ' + error.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleShowManifest = () => {
    setShowManifestDialog(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Load Telemetry Archive
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Import telemetry data from archive files
      </Typography>

      {message && (
        <Alert severity={messageType} sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {/* File Upload */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Archive File</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth
                size="large"
              >
                {selectedFile ? selectedFile.name : 'Select Archive File'}
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              {selectedFile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckIcon color="success" />
                  <Typography variant="body2" color="text.secondary">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Import Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Import Configuration</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Import Mode</InputLabel>
                <Select
                  value={importMode}
                  onChange={(e) => setImportMode(e.target.value)}
                  label="Import Mode"
                >
                  <MenuItem value="append">Append (Add to existing)</MenuItem>
                  <MenuItem value="replace">Replace (Overwrite existing)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {importMode === 'replace' && (
                <FormControl fullWidth>
                  <InputLabel>Confirm Overwrite</InputLabel>
                  <Select
                    value={confirmOverwrite}
                    onChange={(e) => setConfirmOverwrite(e.target.value)}
                    label="Confirm Overwrite"
                  >
                    <MenuItem value={false}>No (Cancel)</MenuItem>
                    <MenuItem value={true}>Yes (Proceed)</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Archive Preview */}
      {archiveManifest && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Archive Preview</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleShowManifest}
              >
                View Details
              </Button>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Fleet: {archiveManifest.fleetInfo.fleetName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customer: {archiveManifest.fleetInfo.customerName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Records: {(archiveManifest.summary?.totalRecords || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Canopies: {archiveManifest.summary?.totalCanopies || 0}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Import Action */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Import Action</Typography>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} /> : <ArchiveIcon />}
            onClick={handleImport}
            disabled={loading || !selectedFile}
            size="large"
          >
            {loading ? 'Importing...' : 'Import Archive'}
          </Button>
        </CardContent>
      </Card>

      {/* Manifest Dialog */}
      <Dialog
        open={showManifestDialog}
        onClose={() => setShowManifestDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Archive Details</DialogTitle>
        <DialogContent>
          {archiveManifest && (
            <Box>
              <Typography variant="h6" gutterBottom>Archive Information</Typography>
              <List>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Archive ID" 
                    secondary={archiveManifest.archiveId} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Export Date" 
                    secondary={new Date(archiveManifest.exportDate).toLocaleString()} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Date Range" 
                    secondary={`${new Date(archiveManifest.dateRange.start).toLocaleDateString()} - ${new Date(archiveManifest.dateRange.end).toLocaleDateString()}`} 
                  />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Canopies</Typography>
              <List>
                {(archiveManifest.canopies || []).map((canopy) => (
                  <ListItem key={canopy.canopyId}>
                    <ListItemIcon><CheckIcon /></ListItemIcon>
                    <ListItemText 
                      primary={`Canopy ${canopy.canopyId}`}
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            {(canopy.recordCount || 0).toLocaleString()} records
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {canopy.dateRange?.start && canopy.dateRange?.end 
                              ? `${new Date(canopy.dateRange.start).toLocaleDateString()} - ${new Date(canopy.dateRange.end).toLocaleDateString()}`
                              : 'No date range available'
                            }
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManifestDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ImportView; 